const DOGSIZE_INPUT_SELECTOR = '.picker-dogsize_input';
// Serving Guide feeding calculator: the weight can be set by dragging the slider or by
// typing in the box, and the offer radios carry the weight range -> variant mapping.
const FEEDING_SLIDER_SELECTOR = '.feeding-calculator__slider';
const FEEDING_WEIGHT_INPUT_SELECTOR = '[data-fc-weight-value]';
const FEEDING_OFFER_SELECTOR = 'input[type="radio"][data-fc-rec-offer]';
// Dragging fires a stream of input events; only act once the buyer settles on a weight.
const WEIGHT_SETTLE_MS = 350;
const VARIANT_PART_SELECTOR = '[data-dp-variant-part]';
const VARIANT_PART_ATTRIBUTE = 'dpVariantPart';
const VARIANT_PARAM = 'variant';
// Dispatched on document.body by the Serving Guide's feeding calculator. Named
// distinctly from the calculator's own constant: both files are classic scripts and
// share one global lexical scope, so a duplicate top-level const is a parse error.
const DP_FEEDING_RESULT_EVENT = 'feedingCalculatorResult';
const SECTIONS_PARAM = 'sections';

// The Section Rendering API renders at most five sections per request, so longer
// lists are split into batches rather than silently truncated.
const MAX_SECTIONS_PER_REQUEST = 5;

/**
 * Keeps the dog-size-dependent sections of the Dental Powder PDP in sync with the
 * dog size the buyer picks.
 *
 * The picker inside the main product section already swaps prices and offer groups
 * on its own. What it cannot do is change content that only Liquid knows about —
 * the carousel image set, the pack size in the title, the vet/superfan portraits,
 * the SuperYolk dose in the ingredients accordion. Those blocks are scoped to a
 * variant server-side (see snippets/dp-dogsize-scope.liquid), so this component
 * re-requests them through the Section Rendering API with the newly selected
 * `?variant=` and swaps the markup in.
 *
 * Two swap modes:
 * - Sections in `data-sections` are replaced in full.
 * - The section in `data-partial-section` (the main product section) keeps its
 *   markup except for the elements tagged `data-dp-variant-part`, so the picker
 *   never loses its state or ProductPicker's cached DOM references.
 */
class DpVariantSections extends HTMLElement {
  constructor() {
    super();
    this.sectionIds = [];
    this.partialSectionId = null;
    this.currentVariantId = null;
    this.abortController = null;
    this.statusElement = null;
    this.onDogsizeChange = this.onDogsizeChange.bind(this);
    this.onFeedingResult = this.onFeedingResult.bind(this);
    this.onWeightInput = this.onWeightInput.bind(this);
    this.weightTimer = null;
    // Set while the calculator is being driven from the picker, so the weight->variant
    // tracking doesn't read that value back and fight the selection. Matters because a
    // bracket's default weight need not fall inside its own range (Over 120 lbs -> 112).
    this.suppressWeightSync = false;
    this.syncPickerToUrl = this.syncPickerToUrl.bind(this);
  }

  connectedCallback() {
    this.sectionIds = this.parseList(this.dataset.sections);
    this.partialSectionId = (this.dataset.partialSection || '').trim() || null;
    this.currentVariantId = (this.dataset.selectedVariantId || '').trim() || null;
    this.statusElement = this.querySelector('[data-dp-status]');

    if (!this.sectionIds.length && !this.partialSectionId) return;

    // Delegated so the listener survives the picker markup being re-rendered.
    document.addEventListener('change', this.onDogsizeChange);
    document.body.addEventListener(DP_FEEDING_RESULT_EVENT, this.onFeedingResult);
    // 'input' covers both the slider drag and typing; 'change' catches a committed
    // value that never fired input (e.g. pasting then tabbing away).
    document.addEventListener('input', this.onWeightInput);
    document.addEventListener('change', this.onWeightInput);

    // ProductPicker is constructed on DOMContentLoaded, and _pdp-picker-dogsizes
    // renumbers the radios at the same point. Ticking an option before either has
    // run would leave the picker's offer groups out of sync with the URL, so the
    // sync waits for that pass to finish. This script is deferred and loads after
    // section-product-main.js, so its handler is queued behind the picker's.
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', this.syncPickerToUrl, { once: true });
    } else {
      this.syncPickerToUrl();
    }
  }

  disconnectedCallback() {
    document.removeEventListener('change', this.onDogsizeChange);
    document.body.removeEventListener(DP_FEEDING_RESULT_EVENT, this.onFeedingResult);
    document.removeEventListener('input', this.onWeightInput);
    document.removeEventListener('change', this.onWeightInput);
    clearTimeout(this.weightTimer);
    document.removeEventListener('DOMContentLoaded', this.syncPickerToUrl);
    this.abortController?.abort();
    this.abortController = null;
  }

  parseList(value) {
    return (value || '')
      .split(',')
      .map((entry) => entry.trim())
      .filter(Boolean);
  }

  /**
   * Reconcile the picker and the scoped sections once the page has settled.
   *
   * With a `?variant=` deep link the server already rendered the right content, so
   * only the picker needs ticking over to match.
   *
   * Without one, Liquid falls back to `selected_or_first_available_variant` — the
   * product's *first* variant — while the picker checks whichever option is marked
   * "Default selected". Block isolation keeps that flag out of Liquid's reach, so
   * when the two disagree the sections are re-rendered for the picker's default.
   */
  syncPickerToUrl() {
    const urlVariantId = new URL(window.location.href).searchParams.get(VARIANT_PARAM);

    if (!urlVariantId) {
      this.syncSectionsToPickerDefault();
      return;
    }

    const input = document.querySelector(`${DOGSIZE_INPUT_SELECTOR}[data-variant-id="${urlVariantId}"]`);
    if (!input || input.checked) return;

    this.currentVariantId = urlVariantId;
    input.checked = true;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /**
   * No deep link: pull the scoped sections up to the picker's default option. Does
   * nothing in the common case where that option is also the product's first variant.
   */
  syncSectionsToPickerDefault() {
    const checked = document.querySelector(`${DOGSIZE_INPUT_SELECTOR}:checked`);
    const variantId = checked?.dataset.variantId;
    if (!variantId || variantId === this.currentVariantId) return;

    this.currentVariantId = variantId;
    this.updateUrl(variantId);
    this.renderSections(variantId, checked.dataset.dogsizeTitle || '');
  }

  /**
   * The Serving Guide calculator resolved a weight to a dog size. Tick that option in
   * the picker and bring the scoped sections with it, so the whole page reflects the
   * weight the buyer just entered.
   */
  onFeedingResult(event) {
    this.selectVariant(event.detail?.variantId);
  }

  /**
   * Follow the weight as it is set in the calculator, by drag or by keyboard, without
   * waiting for Calculate. Debounced so a drag across the track resolves once.
   */
  onWeightInput(event) {
    if (this.suppressWeightSync) return;

    const el = event.target;
    if (!el?.matches?.(`${FEEDING_SLIDER_SELECTOR}, ${FEEDING_WEIGHT_INPUT_SELECTOR}`)) return;

    const weight = parseInt(el.value, 10);
    if (Number.isNaN(weight)) return;

    clearTimeout(this.weightTimer);
    this.weightTimer = setTimeout(() => this.selectVariant(this.variantForWeight(weight)), WEIGHT_SETTLE_MS);
  }

  /**
   * Resolve a weight to a variant using the Feeding Rec Offers' own ranges, so the
   * mapping stays in the merchant's hands rather than being hard-coded here. A blank
   * bound is open-ended, matching how the calculator filters its offers.
   */
  variantForWeight(weight) {
    const match = [...document.querySelectorAll(FEEDING_OFFER_SELECTOR)].find((offer) => {
      const min = parseFloat(offer.getAttribute('data-fc-min-weight'));
      const max = parseFloat(offer.getAttribute('data-fc-max-weight'));
      return (Number.isNaN(min) || weight >= min) && (Number.isNaN(max) || weight <= max);
    });
    return match?.getAttribute('data-variant-id') || null;
  }

  /**
   * Point the whole page at a variant: tick the picker (so ProductPicker refreshes its
   * offers and pricing) and re-render the scoped sections.
   */
  selectVariant(variantId) {
    if (!variantId || variantId === this.currentVariantId) return;

    const input = document.querySelector(`${DOGSIZE_INPUT_SELECTOR}[data-variant-id="${variantId}"]`);
    if (input && !input.checked) {
      input.checked = true;
      // onDogsizeChange takes it from here, which also keeps the picker as the single
      // place that decides what a dog-size change means.
      input.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }

    this.currentVariantId = variantId;
    this.updateUrl(variantId);
    this.renderSections(variantId, input?.dataset.dogsizeTitle || '');
  }

  onDogsizeChange(event) {
    const input = event.target;
    if (!input.matches?.(DOGSIZE_INPUT_SELECTOR)) return;

    const variantId = input.dataset.variantId;
    if (!variantId) return;

    // Move the calculator to this dog size's default weight even when the variant is
    // unchanged, so re-picking an option still resets the slider.
    this.applyDefaultWeightForVariant(variantId);

    if (variantId === this.currentVariantId) return;

    this.currentVariantId = variantId;
    this.updateUrl(variantId);
    this.renderSections(variantId, input.dataset.dogsizeTitle || '');
  }

  /**
   * Point the feeding calculator at the default weight configured on this variant's
   * Feeding Rec Offer. Dispatching `input` on the slider is what makes the calculator
   * redraw its progress fill and sync the weight box; the suppression flag stops that
   * synthetic event coming back round as a variant change.
   */
  applyDefaultWeightForVariant(variantId) {
    const offer = [...document.querySelectorAll(FEEDING_OFFER_SELECTOR)].find(
      (item) => item.getAttribute('data-variant-id') === variantId
    );
    const weight = parseInt(offer?.getAttribute('data-fc-default-weight') || '', 10);
    if (Number.isNaN(weight)) return;

    const slider = document.querySelector(FEEDING_SLIDER_SELECTOR);
    if (!slider || parseInt(slider.value, 10) === weight) return;

    clearTimeout(this.weightTimer);
    this.suppressWeightSync = true;
    slider.value = String(weight);
    slider.dispatchEvent(new Event('input', { bubbles: true }));
    this.suppressWeightSync = false;

    const weightInput = document.querySelector(FEEDING_WEIGHT_INPUT_SELECTOR);
    if (weightInput) weightInput.value = String(weight);
  }

  updateUrl(variantId) {
    const url = new URL(window.location.href);
    url.searchParams.set(VARIANT_PARAM, variantId);
    window.history.replaceState({}, '', url.toString());
  }

  targetSectionIds() {
    const ids = this.partialSectionId ? [this.partialSectionId, ...this.sectionIds] : [...this.sectionIds];
    return [...new Set(ids)];
  }

  async renderSections(variantId, dogsizeTitle) {
    this.abortController?.abort();
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    const ids = this.targetSectionIds();
    this.setLoading(ids, true);

    const batches = [];
    for (let i = 0; i < ids.length; i += MAX_SECTIONS_PER_REQUEST) {
      batches.push(ids.slice(i, i + MAX_SECTIONS_PER_REQUEST));
    }

    try {
      const responses = await Promise.all(batches.map((batch) => this.fetchBatch(batch, variantId, signal)));

      responses.forEach((sections) => {
        Object.entries(sections).forEach(([sectionId, html]) => this.applySection(sectionId, html));
      });

      this.announce(dogsizeTitle);
    } catch (error) {
      // An aborted request is the expected outcome of a fast second click, not a
      // failure. Anything else leaves the previous dog size's content on screen,
      // which stays consistent with the picker until the next successful render.
      if (error.name !== 'AbortError') {
        this.setLoading(ids, false);
        throw error;
      }
      return;
    }

    this.setLoading(ids, false);
  }

  async fetchBatch(sectionIds, variantId, signal) {
    const url = new URL(window.location.pathname + window.location.search, window.location.origin);
    url.searchParams.set(VARIANT_PARAM, variantId);
    url.searchParams.set(SECTIONS_PARAM, sectionIds.join(','));

    const response = await fetch(url.toString(), { signal });
    if (!response.ok) throw new Error(`Section render failed: ${response.status}`);

    return response.json();
  }

  applySection(sectionId, html) {
    const target = document.getElementById(`shopify-section-${sectionId}`);
    if (!target || typeof html !== 'string') return;

    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const source = parsed.getElementById(`shopify-section-${sectionId}`) || parsed.body;

    if (sectionId === this.partialSectionId) {
      source.querySelectorAll(VARIANT_PART_SELECTOR).forEach((nextPart) => {
        const role = nextPart.dataset[VARIANT_PART_ATTRIBUTE];
        const livePart = target.querySelector(`[data-dp-variant-part="${role}"]`);
        if (livePart) livePart.replaceWith(nextPart);
      });
      return;
    }

    target.innerHTML = source.innerHTML;
  }

  setLoading(sectionIds, isLoading) {
    sectionIds.forEach((sectionId) => {
      const target = document.getElementById(`shopify-section-${sectionId}`);
      if (!target) return;
      target.toggleAttribute('data-dp-variant-loading', isLoading);
      if (isLoading) {
        target.setAttribute('aria-busy', 'true');
      } else {
        target.removeAttribute('aria-busy');
      }
    });
  }

  announce(dogsizeTitle) {
    if (!this.statusElement) return;
    this.statusElement.textContent = dogsizeTitle
      ? `Showing content for ${dogsizeTitle}`
      : 'Showing content for the selected dog size';
  }
}

if (!customElements.get('dp-variant-sections')) {
  customElements.define('dp-variant-sections', DpVariantSections);
}
